import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  TextField,
  MenuItem,
  Checkbox,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
  Grid,
} from "@material-ui/core";
import {
  GetApp,
  ExpandMore,
  ExpandLess,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
  DateRange,
  Category,
  Assignment,
} from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles(ctsGuidePortalStyle);

// Month options for period selection
const MONTHS = [
  { value: 1, label: { en: "January", es: "Enero", pt: "Janeiro" } },
  { value: 2, label: { en: "February", es: "Febrero", pt: "Fevereiro" } },
  { value: 3, label: { en: "March", es: "Marzo", pt: "Março" } },
  { value: 4, label: { en: "April", es: "Abril", pt: "Abril" } },
  { value: 5, label: { en: "May", es: "Mayo", pt: "Maio" } },
  { value: 6, label: { en: "June", es: "Junio", pt: "Junho" } },
  { value: 7, label: { en: "July", es: "Julio", pt: "Julho" } },
  { value: 8, label: { en: "August", es: "Agosto", pt: "Agosto" } },
  { value: 9, label: { en: "September", es: "Septiembre", pt: "Setembro" } },
  { value: 10, label: { en: "October", es: "Octubre", pt: "Outubro" } },
  { value: 11, label: { en: "November", es: "Noviembre", pt: "Novembro" } },
  { value: 12, label: { en: "December", es: "Diciembre", pt: "Dezembro" } },
];

// Generate years (current year - 5 to current year + 1)
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

export default function DataRequest() {
  const classes = useStyles();
  const theme = useTheme();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";

  // State
  const [blocks, setBlocks] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState({});
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Period selection
  const [periodStartMonth, setPeriodStartMonth] = useState(1);
  const [periodStartYear, setPeriodStartYear] = useState(currentYear - 1);
  const [periodEndMonth, setPeriodEndMonth] = useState(12);
  const [periodEndYear, setPeriodEndYear] = useState(currentYear);

  // Load all blocks on mount (no industry filter)
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await client.get("/api/ctsguide/data-request/data/");

      // Set blocks
      const blocksData = res.data.blocks || [];
      setBlocks(blocksData);

      // Initialize all blocks as selected by default
      const initialSelection = {};
      const initialExpanded = {};
      blocksData.forEach((block) => {
        initialSelection[block.id] = true;
        initialExpanded[block.id] = false;
      });
      setSelectedBlocks(initialSelection);
      setExpandedBlocks(initialExpanded);
    } catch (error) {
      // Error loading data request
    } finally {
      setLoading(false);
    }
  };

  const handleBlockToggle = (blockId) => {
    setSelectedBlocks((prev) => ({
      ...prev,
      [blockId]: !prev[blockId],
    }));
  };

  const handleBlockExpand = (blockId) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [blockId]: !prev[blockId],
    }));
  };

  const getSelectedBlockIds = useCallback(() => {
    return Object.entries(selectedBlocks)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id);
  }, [selectedBlocks]);

  const getTotalItems = useCallback(() => {
    return blocks
      .filter((block) => selectedBlocks[block.id])
      .reduce((total, block) => total + (block.items_count || 0), 0);
  }, [blocks, selectedBlocks]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const selectedBlockIds = getSelectedBlockIds();

      const response = await client.post(
        "/api/ctsguide/data-request/download/",
        {
          language: lang,
          period_start: `${periodStartYear}-${String(periodStartMonth).padStart(2, "0")}`,
          period_end: `${periodEndYear}-${String(periodEndMonth).padStart(2, "0")}`,
          selected_blocks: selectedBlockIds,
        },
        {
          responseType: "blob",
        },
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `data_request_${lang}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading data request:", error);
    } finally {
      setDownloading(false);
    }
  };

  const getLocalizedText = (obj, fallback = "") => {
    if (!obj) return fallback;
    return obj[lang] || obj.en || obj.es || obj.pt || fallback;
  };

  // Labels for UI
  const labels = {
    title: {
      en: "Data Request Generator",
      es: "Generador de Solicitud de Datos",
      pt: "Gerador de Data Request",
    },
    description: {
      en: "Generate a customized data request template based on the analysis period. Select the data blocks you need and download as Excel.",
      es: "Genere una plantilla de solicitud de datos personalizada según el período de análisis. Seleccione los bloques de datos que necesita y descargue como Excel.",
      pt: "Gere um template de data request customizado baseado no período de análise. Selecione os blocos de dados que precisa e baixe como Excel.",
    },
    analysisPeriod: {
      en: "Analysis Period",
      es: "Período de Análisis",
      pt: "Período de Análise",
    },
    from: {
      en: "From",
      es: "Desde",
      pt: "De",
    },
    to: {
      en: "To",
      es: "Hasta",
      pt: "Até",
    },
    dataBlocks: {
      en: "Data Blocks",
      es: "Bloques de Datos",
      pt: "Blocos de Dados",
    },
    dataBlocksDesc: {
      en: "Select the data groups to include in your data request",
      es: "Seleccione los grupos de datos para incluir en su solicitud",
      pt: "Selecione os grupos de dados para incluir no seu data request",
    },
    items: {
      en: "items",
      es: "ítems",
      pt: "itens",
    },
    downloadExcel: {
      en: "Download Excel",
      es: "Descargar Excel",
      pt: "Baixar Excel",
    },
    noBlocks: {
      en: "No data blocks available.",
      es: "No hay bloques de datos disponibles.",
      pt: "Nenhum bloco de dados disponível.",
    },
    previewTitle: {
      en: "Preview Items",
      es: "Vista Previa de Ítems",
      pt: "Prévia dos Itens",
    },
    itemName: {
      en: "Item Name",
      es: "Nombre del Ítem",
      pt: "Nome do Item",
    },
    description_col: {
      en: "Description",
      es: "Descripción",
      pt: "Descrição",
    },
    format: {
      en: "Format",
      es: "Formato",
      pt: "Formato",
    },
    period: {
      en: "Period",
      es: "Período",
      pt: "Período",
    },
    responsibleArea: {
      en: "Responsible Area",
      es: "Área Responsable",
      pt: "Área Responsável",
    },
    granularity: {
      en: "Granularity",
      es: "Granularidad",
      pt: "Granularidade",
    },
    current: {
      en: "Current",
      es: "Actual",
      pt: "Atual",
    },
  };

  const getLabel = (key) => labels[key]?.[lang] || labels[key]?.en || key;

  const clientColor = theme?.palette?.custom?.clientColor || "#790000";
  const clientTextColor = theme?.palette?.custom?.clientTextColor || "#fff";

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <Assignment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>{getLabel("title")}</h4>
            </CardHeader>
            <CardBody>
              {/* Description */}
              <Box mb={3}>
                <p style={{ color: "#666", fontSize: "0.95em" }}>
                  {getLabel("description")}
                </p>
              </Box>

              {/* Parameters Section */}
              <Card className={classes.sectionCard}>
                <CardBody>
                  <Grid container spacing={3}>
                    {/* Period Start */}
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <DateRange
                          style={{
                            marginRight: "8px",
                            color: clientColor,
                            fontSize: "1.2em",
                          }}
                        />
                        <span style={{ fontWeight: "500" }}>
                          {getLabel("analysisPeriod")} - {getLabel("from")}
                        </span>
                      </Box>
                      <Box display="flex" style={{ gap: "12px" }}>
                        <TextField
                          select
                          value={periodStartMonth}
                          onChange={(e) => setPeriodStartMonth(e.target.value)}
                          variant="outlined"
                          size="small"
                          style={{ width: "180px" }}
                        >
                          {MONTHS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {getLocalizedText(m.label)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          value={periodStartYear}
                          onChange={(e) => setPeriodStartYear(e.target.value)}
                          variant="outlined"
                          size="small"
                          style={{ width: "110px" }}
                        >
                          {YEARS.map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Grid>

                    {/* Period End */}
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <DateRange
                          style={{
                            marginRight: "8px",
                            color: clientColor,
                            fontSize: "1.2em",
                          }}
                        />
                        <span style={{ fontWeight: "500" }}>
                          {getLabel("analysisPeriod")} - {getLabel("to")}
                        </span>
                      </Box>
                      <Box display="flex" style={{ gap: "12px" }}>
                        <TextField
                          select
                          value={periodEndMonth}
                          onChange={(e) => setPeriodEndMonth(e.target.value)}
                          variant="outlined"
                          size="small"
                          style={{ width: "180px" }}
                        >
                          {MONTHS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {getLocalizedText(m.label)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          value={periodEndYear}
                          onChange={(e) => setPeriodEndYear(e.target.value)}
                          variant="outlined"
                          size="small"
                          style={{ width: "110px" }}
                        >
                          {YEARS.map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Grid>
                  </Grid>
                </CardBody>
              </Card>

              {/* Data Blocks Section */}
              <Card className={classes.sectionCard}>
                <CardHeader
                  style={{
                    backgroundColor: clientColor,
                    color: clientTextColor,
                    padding: "15px 20px",
                    borderRadius: "4px 4px 0 0",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Category style={{ marginRight: "10px" }} />
                    <span style={{ fontWeight: "500", fontSize: "1.1em" }}>
                      {getLabel("dataBlocks")}
                    </span>
                  </Box>
                  <p
                    style={{
                      margin: "5px 0 0 0",
                      opacity: 0.9,
                      fontSize: "0.85em",
                    }}
                  >
                    {getLabel("dataBlocksDesc")}
                  </p>
                </CardHeader>
                <CardBody style={{ padding: 0 }}>
                  {loading ? (
                    <Box className={classes.loadingContainer}>
                      <CircularProgress style={{ color: clientColor }} />
                    </Box>
                  ) : blocks.length === 0 ? (
                    <Box textAlign="center" p={4}>
                      <p>{getLabel("noBlocks")}</p>
                    </Box>
                  ) : (
                    <Box>
                      {blocks.map((block) => (
                        <Box
                          key={block.id}
                          style={{
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          {/* Block Header */}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            p={2}
                            style={{
                              backgroundColor: selectedBlocks[block.id]
                                ? "rgba(121, 0, 0, 0.03)"
                                : "transparent",
                              cursor: "pointer",
                            }}
                            onClick={() => handleBlockExpand(block.id)}
                          >
                            <Box display="flex" alignItems="center">
                              <Checkbox
                                checked={selectedBlocks[block.id] || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleBlockToggle(block.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  color: clientColor,
                                  marginRight: "10px",
                                }}
                                icon={<CheckBoxOutlineBlank />}
                                checkedIcon={<CheckBoxIcon />}
                              />
                              <Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  style={{ gap: "10px" }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "500",
                                      fontSize: "1em",
                                      color: selectedBlocks[block.id]
                                        ? clientColor
                                        : "#333",
                                    }}
                                  >
                                    {getLocalizedText(
                                      block.name,
                                      block.meta_name,
                                    )}
                                  </span>
                                </Box>
                                {block.description &&
                                  getLocalizedText(block.description) && (
                                    <p
                                      style={{
                                        margin: "4px 0 0 0",
                                        fontSize: "0.85em",
                                        color: "#666",
                                      }}
                                    >
                                      {getLocalizedText(block.description)}
                                    </p>
                                  )}
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center">
                              <Chip
                                size="small"
                                label={`${block.items_count || 0} ${getLabel("items")}`}
                                style={{
                                  marginRight: "10px",
                                  backgroundColor: "#f5f5f5",
                                  fontSize: "0.75em",
                                }}
                              />
                              <IconButton size="small">
                                {expandedBlocks[block.id] ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )}
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Block Items (Expanded) */}
                          <Collapse in={expandedBlocks[block.id]}>
                            <Box
                              style={{
                                backgroundColor: "#fafafa",
                                borderTop: "1px solid #eee",
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8em",
                                        padding: "10px 16px",
                                      }}
                                    >
                                      {getLabel("itemName")}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8em",
                                        padding: "10px 16px",
                                      }}
                                    >
                                      {getLabel("description_col")}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8em",
                                        padding: "10px 16px",
                                      }}
                                    >
                                      {getLabel("format")}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8em",
                                        padding: "10px 16px",
                                      }}
                                    >
                                      {getLabel("responsibleArea")}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8em",
                                        padding: "10px 16px",
                                      }}
                                    >
                                      {getLabel("granularity")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(block.items || []).map((item) => (
                                    <TableRow key={item.id}>
                                      <TableCell
                                        style={{
                                          fontSize: "0.85em",
                                          padding: "8px 16px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        {getLocalizedText(item.name)}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "0.85em",
                                          padding: "8px 16px",
                                          maxWidth: "300px",
                                        }}
                                      >
                                        {getLocalizedText(
                                          item.effective_description ||
                                            item.description,
                                        )}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "0.85em",
                                          padding: "8px 16px",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {item.suggested_format}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "0.85em",
                                          padding: "8px 16px",
                                        }}
                                      >
                                        {getLocalizedText(
                                          item.responsible_area,
                                        )}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "0.85em",
                                          padding: "8px 16px",
                                        }}
                                      >
                                        {getLocalizedText(
                                          item.effective_granularity ||
                                            item.expected_granularity,
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Download Section */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
              >
                <Box>
                  <span style={{ color: "#666", fontSize: "0.9em" }}>
                    {getSelectedBlockIds().length}{" "}
                    {getLabel("dataBlocks").toLowerCase()} · {getTotalItems()}{" "}
                    {getLabel("items")}
                  </span>
                </Box>
                <Button
                  color="rose"
                  onClick={handleDownload}
                  disabled={getSelectedBlockIds().length === 0 || downloading}
                  style={{ backgroundColor: clientColor }}
                >
                  {downloading ? (
                    <CircularProgress
                      size={20}
                      style={{ color: clientTextColor, marginRight: "10px" }}
                    />
                  ) : (
                    <GetApp style={{ marginRight: "10px" }} />
                  )}
                  {getLabel("downloadExcel")}
                </Button>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="dataRequest" />
    </div>
  );
}
